/**
 * Per-platform enable/disable — the "attached but paused" state.
 *
 * A post's `platforms: string[]` is the ATTACHED set (each keeps its
 * `platform_captions` override + `media_keys.perPlatform` crop/aspect data).
 * `media_keys.disabledPlatforms` is a sidecar list of attached platforms that
 * should be SKIPPED at the next send — absent/empty means every attached
 * platform is enabled, so every pre-existing post is unaffected (no migration).
 *
 * Removing a platform from `platforms[]` still deletes it (and its sidecar
 * data); disabling only pauses it, so captions/crops survive a re-enable.
 *
 * Read at dispatch (process-scheduled-posts) so paused platforms don't publish.
 */

type MediaKeysLike = { disabledPlatforms?: unknown } | null | undefined;

function normalizeStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string' && v.length > 0)
    : [];
}

/** The attached platforms explicitly paused for the next send. */
export function getDisabledPlatforms(mediaKeys: MediaKeysLike): string[] {
  return normalizeStringArray(mediaKeys?.disabledPlatforms);
}

/** True when `platform` is attached-and-active (not in the disabled sidecar). */
export function isPlatformEnabled(mediaKeys: MediaKeysLike, platform: string): boolean {
  return !getDisabledPlatforms(mediaKeys).includes(platform);
}

/**
 * The actual send set: attached platforms minus the disabled sidecar. Order
 * follows `platforms` (the attached array), not the sidecar.
 */
export function enabledPlatforms(platforms: string[], mediaKeys: MediaKeysLike): string[] {
  const disabled = new Set(getDisabledPlatforms(mediaKeys));
  return platforms.filter((p) => !disabled.has(p));
}

/**
 * Immutable write: return a new `media_keys` object with `platform` toggled to
 * `enabled`. Prunes the sidecar to empty rather than leaving `[]` cruft.
 * Never mutates the input.
 */
export function withPlatformEnabled<T extends Record<string, unknown>>(
  mediaKeys: T | null | undefined,
  platform: string,
  enabled: boolean,
): T {
  const base = (mediaKeys ?? {}) as T;
  const current = getDisabledPlatforms(base as MediaKeysLike);
  const next = enabled
    ? current.filter((p) => p !== platform)
    : current.includes(platform)
      ? current
      : [...current, platform];
  const result = { ...base } as Record<string, unknown>;
  if (next.length === 0) delete result.disabledPlatforms;
  else result.disabledPlatforms = next;
  return result as T;
}
